/**
 * Internal: build the backend `ThemeContentInput` (whole-theme write shape) from
 * a command's write fields + the 6 hexes. When a recipe is present its DERIVED
 * hexes win — that's the "publish the generated 6-hex output" rule: the 6 hexes
 * are what every app renders.
 *
 * The recipe ITSELF (treatment · seed · polarity · font) rides along in
 * `ThemeContentInput.treatment` (#4827, on master since 2026-07-08; `ThemeFields`
 * reads `Theme.treatment`, so the gql.tada schema snapshot is the contract).
 * Omit-vs-null is deliberate (see `toContentInput`).
 */
import type { ThemeContentInput } from "@/data/brands-api";
import { pickColours, type Theme } from "@/data/config";
import { recipeToLegacyHexes, type LegacyHexes, type ThemeRecipe } from "@/data/theme-engine";
import { matchingVerdicts, type PinVerdict, type PinVerdictReport } from "@/data/pin-verdicts";
import type { SaveThemeInput, ThemeWriteFields } from "./types";

export function resolveHexes(hexes: LegacyHexes, recipe?: ThemeRecipe | null): LegacyHexes {
  return recipe ? recipeToLegacyHexes(recipe) : hexes;
}

/** `treatment` for "no recipe": the backend stores any JSON object as-is (only
 *  omitted/null leave the stored one alone) and the app reads `{}` as none, so
 *  publishing this clears a stored recipe. */
export const CLEARED_RECIPE = "{}";

/** Serialise a recipe for the backend `treatment` text column (#4827). Explicit
 *  field list so the persisted JSON shape is stable even if `ThemeRecipe` grows. */
export function serializeRecipe(recipe: ThemeRecipe): string {
  return JSON.stringify({
    treatmentId: recipe.treatmentId,
    seed: recipe.seed,
    polarity: recipe.polarity,
    fontId: recipe.fontId,
  });
}

/**
 * Build the whole-theme write input. The recipe is written to `treatment` when
 * there IS one, {@link CLEARED_RECIPE} when `clearRecipe` asks; otherwise the key
 * is **omitted entirely**, never sent as `null`. The backend treats both as
 * "leave the stored recipe alone", but an absent key says so plainly.
 */
export function toContentInput(
  fields: ThemeWriteFields,
  hexes: LegacyHexes,
  recipe?: ThemeRecipe | null,
  clearRecipe = false,
): ThemeContentInput {
  const c = resolveHexes(hexes, recipe);
  const content: ThemeContentInput = {
    name: fields.name,
    header: c.header,
    background: c.background,
    surface: c.surface,
    primary: c.primary,
    accent: c.accent,
    text: c.text,
    logoText: fields.logoText,
    logoImg: fields.logoImg ?? null,
  };
  if (recipe) content.treatment = serializeRecipe(recipe);
  else if (clearRecipe) content.treatment = CLEARED_RECIPE;
  return content;
}

/**
 * A stored theme's write parts — its fields, the six colours the editor shows
 * and which of them the server holds as null. The one mapper from a UI `Theme`
 * to a write: Save & publish and a rename both go through it and
 * {@link composeThemeWrite}, which writes an unset colour as null.
 */
export function themeWriteParts(
  theme: Theme,
): Pick<SaveThemeInput, "fields" | "hexes" | "unsetColors"> {
  return {
    fields: { name: theme.name, logoText: theme.logoText, logoImg: theme.logoImg },
    hexes: pickColours(theme),
    unsetColors: theme.unsetColors,
  };
}

/**
 * `pinned` (product#116), "clear": a colour the app REFUSED is written as `null`
 * (the app paints its own colour there) instead of being saved. Placed pins and
 * the recipe are written as-is. The app's report applies only when it judged
 * exactly these six colours; otherwise (no answer yet, or an answer about an
 * older draft) the content is unchanged — publish as today. Obeys the app's
 * verdicts, never recomputes the rule. Returns the content to write and which
 * pins were cleared, so the caller can say so.
 */
export function clearRefusedPins(
  content: ThemeContentInput,
  report?: PinVerdictReport | null,
  /** The colours the preview showed, when they differ from `content` (the UI
   *  draft shows a placeholder where `content` writes null). */
  shown: Partial<Record<keyof PinVerdictReport["colors"], string | null>> = content,
): { content: ThemeContentInput; cleared: PinVerdict["pin"][] } {
  const cleared = matchingVerdicts(report, shown)
    .filter((v) => !v.placed)
    .map((v) => v.pin);
  if (!cleared.length) return { content, cleared };
  const next = { ...content };
  for (const pin of cleared) next[pin] = null;
  return { content: next, cleared };
}

/**
 * The whole write a save/publish sends: the content (recipe-derived hexes and
 * the recipe itself when there is one, see {@link toContentInput}), with a
 * colour the server holds as null kept null and a refused pin cleared. The
 * report is matched against the six colours the preview showed — the
 * placeholder where a kept-null colour writes null.
 */
export function composeThemeWrite(
  input: Omit<SaveThemeInput, "brandId" | "themeId">,
): { content: ThemeContentInput; cleared: PinVerdict["pin"][] } {
  const content = toContentInput(input.fields, input.hexes, input.recipe, input.clearRecipe);
  if (!input.recipe) for (const k of input.unsetColors ?? []) content[k] = null;
  return clearRefusedPins(content, input.pinVerdicts, resolveHexes(input.hexes, input.recipe));
}

/** A stored recipe (`Theme.treatment`, as read back), or null when none or unreadable. */
export function parseRecipe(storedTreatment: string | null | undefined): ThemeRecipe | null {
  if (!storedTreatment) return null;
  try {
    const r = JSON.parse(storedTreatment) as ThemeRecipe;
    return r && typeof r === "object" && r.treatmentId && r.seed ? r : null;
  } catch {
    return null;
  }
}

/**
 * Whether a stored recipe is exactly this one. The server stores it as `jsonb`
 * (key order not kept), so compare the parsed fields, not the strings.
 */
export function recipeReadBack(storedTreatment: string | null | undefined, recipe: ThemeRecipe): boolean {
  const stored = parseRecipe(storedTreatment);
  return !!stored && serializeRecipe(stored) === serializeRecipe(recipe);
}

/** Recipe fields flattened for an analytics event (or {} when there's no recipe). */
export function recipeProps(recipe?: ThemeRecipe | null): Record<string, unknown> {
  return recipe
    ? { treatmentId: recipe.treatmentId, seed: recipe.seed, polarity: recipe.polarity, fontId: recipe.fontId }
    : {};
}
